<template>
    <ImageIcon :src="imgUrl" :title="data.title" :alt="data.filename ? data.filename : ''" :extension="data.extension" v-if="!loading && imgUrl!== ''" :style="style" :class="props.class" :userImage="userImage" :imageErrorOccured="imageLoadError"/>
    <SkelatonVue v-else :style="style" :class="props.class"/>
</template>

<script setup>
    import { onMounted, onBeforeUnmount, defineProps, inject, ref, defineEmits, watch, nextTick, getCurrentInstance } from 'vue';
    import ImageIcon from "@/components/atom/ImageIcon/ImageIcon.vue"
    import * as env from '@/config/env';
    import isEqual from "lodash/isEqual"
    import SkelatonVue from '../Skelaton/Skelaton.vue';
    import { apiRequest, apiRequestWithoutCompnay } from '../../../services';
    import {storageQueryBuilder} from '@/utils/storageQueryBuild.js';
    import { imageUrlCache, pendingImageUrlRequests } from './imageRequestCache.js';
    const defaultUserAvatar = inject("$defaultUserAvatar")
    const defaultImage = require("@/assets/images/image_name.png")
    // eslint-disable-next-line
    const companyId = inject('$companyId');  
    const props = defineProps({
        data: {
            type: Object,
            required: true,
        },
        class: {},
        companyId: {
            type: String,
            default: ""
        },
        style: {},
        thumbnail: {
            type: String,
            default: ""
        },
        userImage: {
            type: Boolean,
            default: false
        },
        fromWhere: {
            type: String,
            default: ""
        }
    });

    const emit = defineEmits(["downloadUrl"]);
    const imageObject = ref();
    const imgUrl = ref("");
    const loading = ref(false);
    const tuhumbnailSize = ref(props.thumbnail);
    const isthumbnailOriginal = ref(false);
    const isVisible = ref(false);
    const componentInstance = getCurrentInstance();
    let visibilityObserver = null;
    onMounted(() => {
        imageObject.value = JSON.parse(JSON.stringify(props.data))
        scheduleImageLoad();
    })

    onBeforeUnmount(() => {
        if (visibilityObserver) {
            visibilityObserver.disconnect();
            visibilityObserver = null;
        }
    })

    watch(() => props.thumbnail,(thumb) => {
        tuhumbnailSize.value = thumb;
        if(thumb && isVisible.value){
            getImage();
        }
        isthumbnailOriginal.value = false;
    })

    watch(() => props.data, (newVal, oldVal) => {
        if(!isEqual(newVal, oldVal) && props.fromWhere == '') {
            imageObject.value = JSON.parse(JSON.stringify(props.data))
            if (isVisible.value) {
                getImage(newVal.url !== oldVal.url);
            }
            isthumbnailOriginal.value = false;
        }
    })

    function scheduleImageLoad() {
        if (isVisible.value) {
            getImage();
            return;
        }

        if (typeof IntersectionObserver === "undefined") {
            isVisible.value = true;
            getImage();
            return;
        }

        nextTick(() => {
            const target = componentInstance?.proxy?.$el;
            if (!target) {
                isVisible.value = true;
                getImage();
                return;
            }

            visibilityObserver = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    isVisible.value = true;
                    visibilityObserver?.disconnect();
                    visibilityObserver = null;
                    getImage();
                }
            }, {
                rootMargin: "250px"
            });
            visibilityObserver.observe(target);
        });
    }

    function getFileURl(filePath, useThumbnail = true) {
        return new Promise((resolve,reject)=>{
            let bucketId = props.userImage ? "USER_PROFILES" : (props.companyId?.length ? props.companyId : companyId.value);
            let path = '';
            if (tuhumbnailSize.value && useThumbnail) {
                const lastDotIndex = filePath.lastIndexOf(".");
                const filename = filePath.substring(0, lastDotIndex);
                const extension = filePath.substring(lastDotIndex + 1);
                path = `${filename}-${tuhumbnailSize.value}.${extension}`;
            } else {
                path = filePath
            }
            let axiousObject = storageQueryBuilder('get',bucketId,path);
            if(filePath?.startsWith('data:') == false) {
                apiRequest(axiousObject.method,axiousObject.route).then((res)=>{
                    resolve(res);
                }).catch((error) => {
                    reject(error);
                });
            }
        })
    }
    function getImage(isLoad = true,isThumbnailRetry = false) {
        if(isLoad){
            loading.value = true;
        }
        const cid = props.companyId?.length ? props.companyId : companyId.value;
        const sourcePath = props.data.url || props.data.path || props.data.objectKey || props.data.object_key || '';
        if (!sourcePath) {
            handleImageUnavailable();
            return;
        }
        if (/^https?:\/\//i.test(sourcePath)) {
            imgUrl.value = sourcePath;
            emit("downloadUrl", sourcePath);
            loading.value = false;
            return;
        }
        let path = ''
        if (tuhumbnailSize.value && !isThumbnailRetry) {
            const lastDotIndex = sourcePath.lastIndexOf(".");
            const filename = sourcePath.substring(0, lastDotIndex);
            const extension = sourcePath.substring(lastDotIndex + 1);
            if(extension !== 'pdf'){
                path = `${filename}-${tuhumbnailSize.value}.${extension}`;
            }else{
                path = sourcePath
            }
        } else {
            path = sourcePath
        }
        if (path && path.includes("base64")) {
            if(props.userImage){
                imgUrl.value = props.data.url;
                return loading.value = false
            }else{
                imgUrl.value = path;
                if(imgUrl.value.includes('undefined')){
                    imgUrl.value = imgUrl.value.slice(0, imgUrl.value.length - 18)
                }
                return loading.value = false;
            }
        }

        if(env.STORAGE_TYPE && env.STORAGE_TYPE == 'server') {
            const cacheKey = `server:${props.userImage ? "USER_PROFILES" : cid}:${path}`;
            if (imageUrlCache.has(cacheKey)) {
                const cachedUrl = imageUrlCache.get(cacheKey);
                imageObject.value.downloadUrl = cachedUrl;
                emit("downloadUrl", cachedUrl + "&download=true");
                imgUrl.value = cachedUrl;
                loading.value = false;
                return;
            }
            const request = pendingImageUrlRequests.get(cacheKey) || getFileURl(sourcePath, !isThumbnailRetry);
            pendingImageUrlRequests.set(cacheKey, request);
            request.then((response)=>{
                if (!response?.data?.url) {
                    handleImageUnavailable();
                    return;
                }
                imageUrlCache.set(cacheKey, response.data.url);
                imageObject.value.downloadUrl = response.data.url;
                emit("downloadUrl",response.data.url + "&download=true");
                imgUrl.value = response.data.url;
                loading.value = false;
            }).catch((err)=>{
                console.error(err,"error in get image data");
                handleImageUnavailable();
            }).finally(() => {
                pendingImageUrlRequests.delete(cacheKey);
            });
            return;
        }
        const formData = {
            companyId: cid,
            path: path
        }
        const cacheKey = `${props.userImage ? "user" : "object"}:${cid}:${path}`;
        if (imageUrlCache.has(cacheKey)) {
            const cachedUrl = imageUrlCache.get(cacheKey);
            imageObject.value.downloadUrl = cachedUrl;
            emit("downloadUrl", cachedUrl)
            imgUrl.value = cachedUrl;
            loading.value = false;
            return;
        }
        if (pendingImageUrlRequests.has(cacheKey)) {
            pendingImageUrlRequests.get(cacheKey).then((cachedUrl) => {
                imageObject.value.downloadUrl = cachedUrl;
                emit("downloadUrl", cachedUrl)
                imgUrl.value = cachedUrl;
                loading.value = false;
            }).catch((err) => {
                console.error(err,"error in get image data");
                handleImageUnavailable();
            });
            return;
        }
        const url = props.userImage ? env.WASABI_RETRIVE_USER_PROFILE : env.WASABI_RETRIVE_OBJECT;
        let reqAPi;
        if (props.userImage) {
            reqAPi = apiRequestWithoutCompnay("get", `${url}/${cid}/${path}`, formData);
        } else if (formData.path) {
            reqAPi = apiRequest("post", url, formData);
        } else {
            loading.value = false;
            return;
        }
        const pendingRequest = reqAPi.then((response) => {
            if (response.data.status === false) {
                return "";
            }
            return response.data.status === true ? response.data.statusText : "";
        });
        pendingImageUrlRequests.set(cacheKey, pendingRequest);
        pendingRequest.then((url)=>{
            if(url){
                imageUrlCache.set(cacheKey, url);
                imageObject.value.downloadUrl = url;
                emit("downloadUrl",url)
                imgUrl.value = url;
                loading.value = false;
             } else {
                handleImageUnavailable();
             }
         }).catch((err)=>{
            console.error(err,"error in get image data");
            handleImageUnavailable();
         }).finally(() => {
             pendingImageUrlRequests.delete(cacheKey);
         })
     }

    function handleImageUnavailable() {
        if (tuhumbnailSize.value && !isthumbnailOriginal.value) {
            isthumbnailOriginal.value = true;
            getImage(true, true);
            return;
        }

        imgUrl.value = props.userImage ? defaultUserAvatar : defaultImage;
        loading.value = false;
    }

    function imageLoadError() {
        if (tuhumbnailSize.value) {
            if (isthumbnailOriginal.value == false) {
                getImage(true,true);
                isthumbnailOriginal.value = true;
            } else {
                if (props.userImage) {
                    imgUrl.value = defaultUserAvatar
                } else {
                    imgUrl.value = defaultImage
                }
            }
        } else {
            if (props.userImage) {
                imgUrl.value = defaultUserAvatar
            } else {
                imgUrl.value = defaultImage
            }
        }
    }
</script> 
